import { Loader2Icon } from "lucide-react";
import { ContextChip } from "./context-chip";
import {
  AttachmentVariant,
  type PromptAddon,
  type PromptAddonType,
} from "@/stores/slices/prompt-context.slice";
import { useDeleteFile } from "@/hooks/use-delete-file";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { imagineStore } from "@/stores/imagine.store";
import { DocumentIcon } from "@heroicons/react/16/solid";

type AttachmentChipProps = {
  addon: Extract<PromptAddon, { type: PromptAddonType.ATTACHMENT }>;
};

export function AttachmentChip({ addon }: AttachmentChipProps) {
  const { id, props } = addon;
  const deleteFile = useDeleteFile();
  const removeAddon = imagineStore.use.promptContext.removeAddon();

  const handleRemove = () => {
    removeAddon(id);
    void deleteFile(props.url); // Best effort to delete the file
  };

  const filename = props.filename ?? "Unnamed attachment";

  const icon =
    props.uploadStatus === "uploading" ? (
      <Loader2Icon className="size-4 animate-spin" />
    ) : props.variant === AttachmentVariant.IMAGE ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="size-4.5 overflow-hidden flex items-center justify-center">
            <img
              className="size-full object-cover p-[0.5px] bg-foreground/5 border border-foreground/7 rounded-[5px]"
              src={props.url}
              alt={filename}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent className="bg-background max-w-96 border border-border shadow-sm overflow-hidden pointer-events-none rounded-md p-4">
          <div className="flex flex-col items-center justify-center gap-4">
            <img
              src={props.url}
              alt={filename}
              className="rounded-md bg-background"
            />
            <p className="text-xs text-muted-foreground truncate max-w-80">
              {filename}
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    ) : (
      <DocumentIcon className="size-4" />
    );

  return (
    <ContextChip
      label={filename}
      icon={icon}
      onRemove={props.removable ? handleRemove : undefined}
    />
  );
}
